// src/components/Dashboard/ClaimChecksTab.jsx
//
// Sección "Cheques" de la ficha del reclamo: todos los cheques del reclamo en
// un solo lugar, cada uno con sus archivos (copia del cheque, comprobantes de
// pago y respaldo en el CRM histórico) para verlos o descargarlos. Solo lee:
// registrar cheques y avanzar sus pasos sigue siendo de la sección Pasos.
import React, { useEffect, useMemo, useState } from 'react';
import { getChecksForClaimRecord } from '../../services/claimService';
import CheckFiles from './CheckFiles';
import { useAuth } from '../../contexts/AuthContext';
import { money } from '../../documents/closingData';

const millis = t => (t?.toMillis ? t.toMillis() : 0);

// active: la ficha conserva esta pestaña montada aunque esté oculta; al volver a ella se relee
// la lista, porque desde Pasos se pueden registrar cheques o subir archivos entre una visita y otra.
const ClaimChecksTab = ({ claimRecord, isAdmin, language, active = true }) => {
  const es = language === 'es';
  const { userAdjusterName, userCanViewAllClaims, userRoleKey, can } = useAuth();
  const [checks, setChecks] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setLoadFailed(false);
    getChecksForClaimRecord(claimRecord, isAdmin, { name: userAdjusterName, canViewAll: userCanViewAllClaims, summaryOnly: userRoleKey === 'csr_assistant' })
      .then(list => { if (!cancelled) setChecks([...list].sort((a, b) => millis(a.createdAt) - millis(b.createdAt))); })
      .catch(error => { console.error('Error loading checks:', error); if (!cancelled) { setChecks([]); setLoadFailed(true); } });
    return () => { cancelled = true; };
  }, [claimRecord.id, reloadKey, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = useMemo(() => (checks || []).reduce((sum, c) => sum + (Number(c.amount) || 0), 0), [checks]);

  return (
    <div className="flow-block checks-panel">
      <div className="closing-head">
        <div>
          <div className="closing-head-title">{es ? 'Cheques' : 'Checks'}</div>
          {checks && checks.length > 0 && (
            <div className="flow-muted">
              {es ? `${checks.length} cheque(s) · ${money(total) || '$0'} en total` : `${checks.length} check(s) · ${money(total) || '$0'} total`}
            </div>
          )}
        </div>
      </div>

      {checks === null && <p className="flow-note">{es ? 'Cargando cheques…' : 'Loading checks…'}</p>}
      {loadFailed && (
        <div role="alert" className="cd-error">
          <i className="fa-solid fa-exclamation-triangle"></i> {es ? 'No se pudieron cargar los cheques.' : 'Could not load the checks.'}{' '}
          <button type="button" className="cd-btn" onClick={() => { setChecks(null); setReloadKey(k => k + 1); }}>
            <i className="fa-solid fa-rotate-right"></i> {es ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}
      {checks && checks.length === 0 && !loadFailed && (
        <p className="flow-empty">
          <i className="fa-solid fa-money-check-dollar"></i> {es ? 'Este reclamo todavía no tiene cheques.' : 'This claim has no checks yet.'}
        </p>
      )}

      <div className="checks-list">
        {(checks || []).map(check => {
          return (
            <div key={check.id} className="check-card is-open">
              <div className="check-card-head">
                <span className="check-card-icon"><i className="fa-solid fa-money-check-dollar"></i></span>
                <span className="check-card-main">
                  <span className="check-card-title">
                    {money(check.amount) || '$0'} · {check.checkNumber ? `${es ? 'Cheque' : 'Check'} #${check.checkNumber}` : (es ? 'Cheque sin número' : 'Check without number')}
                  </span>
                  <span className="check-card-meta">
                    {[check.checkDate, check.payableTo,
                      can('viewEarnings') && check.invoiceNumber && `Invoice ${check.invoiceNumber}`,
                      can('viewEarnings') && (check.paid || check.archived ? (es ? 'Comisión cobrada' : 'Commission collected') : (es ? 'Cobro CSR pendiente' : 'CSR collection pending'))]
                      .filter(Boolean).join(' · ')}
                  </span>
                </span>
              </div>
              <div className="check-card-body">
                <CheckFiles check={check} es={es} download />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClaimChecksTab;
